
import React, { useState, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { openOrganisationForm } from 'services/forms';
import './_users.scss';
import {
  OverflowMenu,
  OverflowMenuItem,
  Row,
  Column,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Layer,
  Grid
} from '@carbon/react';
import { Page } from 'components/Page';
import { CustomDataTable } from 'components/Datatable';
import {
  Panel,
  PanelFooter,
  PanelHeader,
  PanelLabel,
  PanelLink,
} from 'components/Panel';
import { SearchBar } from 'components/SearchBar';

import moment from 'moment';
import 'moment/locale/de';

import useUser from '@/lib/hooks/useUser';
import { useGetOrganisationsWithPaginationQuery } from 'services/organisations';

moment.locale('de');

const columns = [
  {
    id: 'name',
    title: 'Name',
    sortCycle: 'tri-states-from-ascending',
  },
  {
    id: 'DeliveryAddress__street',
    title: 'Anschrift',
    sortCycle: 'tri-states-from-ascending',
  },
  {
    id: 'DeliveryAddress__city',
    title: 'Stadt',
    sortCycle: 'tri-states-from-ascending',
  },
  {
    id: 'count_users',
    title: 'Benutzer',
  },
  {
    id: 'count_locations',
    title: 'Standorte',
  }
];

const getRowItems = (rows, isAdmin = false) => {
  return rows.map((row) => {
    let a = {
      id: row.model.ID,
      name: row.name,
      count_users: (row.users || []).length,
      count_locations: (row.locations || []).length,
      selected: false,
      users: row.users || [], // to get more fields inside expanded rows
      locations: row.locations || [], // to get more fields inside expanded rows
    };

    a['DeliveryAddress__city'] = row.delivery_address?.city;
    a['DeliveryAddress__street'] = row.delivery_address?.street;
    return a;
  });
};

const RowActions = ({ row }) => {
  const dispatch = useDispatch();

  const handleEdit = (orgID) => {
    dispatch(openOrganisationForm(orgID));
  };

  // The placeholder entries "Benutzer hinzufügen", "Benutzer anzeigen",
  // "Standorte anzeigen" and "Organisation löschen" were all wired to the
  // edit action (or had no onClick at all). Removed for now until each gets
  // its own implementation, otherwise the operator gets the wrong dialog.
  return (
    <OverflowMenu flipped aria-label="Menü">
      <OverflowMenuItem
        itemText="Bearbeiten"
        onClick={() => {
          handleEdit(row.id);
        }}
      />
    </OverflowMenu>
  );
};

const OrganisationsTable = () => {
  const {isAdmin} = useUser();

  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState({ columnId: 'name', direction: 'DESC' });
  const [filterBy, setFilter] = useState(null);
  const [search, setSearch] = useState(null);
  const [query, setQuery] = useState({pageIndex: page, search: search, order_by: sortBy, pageSize: pageSize});
  const {
    data = {data: {records: [], total_record: 0}},
    isFetching,
  } = useGetOrganisationsWithPaginationQuery(query, {refetchOnMountOrArgChange: false});


  useEffect(() => {
    //setQuery(f => { return {...f, filters: filterBy ? [{ field: 'Role.name', value: filterBy }] : null, pageIndex: 1}});
  }, [filterBy]);

  const searchChanged = (text) => {
    setSearch(text);
  };

  useEffect(() => {
    if (sortBy && sortBy.length > 0) {
      const a = sortBy[0].split(' ');
      setSortBy({ sortId: a[0], direction: a[1] || 'ASC' });
    }
  }, [sortBy]);

  useEffect(() => {
    if (data.data.records && data.data.records.length > 0) {
      setRows(getRowItems(data.data.records || [], [], isAdmin, /*rows*/));
    }
  }, [data, isAdmin]);

  return (
    <>
      <SearchBar searchChanged={searchChanged} labelText={"Organisation suchen..."}>
      </SearchBar>
      <CustomDataTable
        columns={columns.slice(
          0,
          isAdmin ? columns.length : columns.length - 1
        )}
        search={search}
        rows={rows}
        sortInfo={sortBy}
        loadItems={({
          pageSize: newPageSize,
          pageIndex: newPageIndex,
          sortBy: newSortBy,
        }) => {
          setPageSize(newPageSize);
          setQuery(f => { return {...f, pageIndex: newPageIndex, search: search, sortBy: newSortBy, pageSize: newPageSize}});
          setPage(newPageIndex);
          if (newSortBy !== sortBy && newSortBy !== undefined) {
            setSortBy(newSortBy);
          }
        }}
        totalItems={data.data?.total_record || 0}
        pageSize={pageSize}
        page={page}
        customRowActionsComponent={RowActions}
        isLoading={isFetching}
      />

      <br/>
      <br/>
    </>
  );
};

export const Organisations = () => {
  const dispatch = useDispatch();

  return (
    <Page
      pageClass="users-page"
      mainContentClass="users-page__r2"
      pageTitle="Organisationen Übersicht"
    >
      <Layer>
        <Row>
          <Column md={5}>
            <Row className="small_row">
              <Column md={4}>
                <Panel halfSize>
                  <PanelHeader header>
                    Organisation hinzufügen
                  </PanelHeader>
                  <PanelLabel>
                    Benutzer müssen Organisationen hinzugefügt werden. Diese müssen also zuerst angelegt werden.
                  </PanelLabel>
                  <PanelFooter>
                    <PanelLink onClick={() => dispatch(openOrganisationForm("new"))}>
                      ORGANISATION HINZUFÜGEN &gt;
                    </PanelLink>
                  </PanelFooter>
                </Panel>
              </Column>
            </Row>
          </Column>
        </Row>
        <Row>
          <Column>
            <Layer>
              <OrganisationsTable />
            </Layer>
          </Column>
        </Row>
      </Layer>
    </Page>
  );
};

const RowDetails = ({item: organisation, editLocation = () => {}, editUser = () => {} }) => {
  console.info("RowDetails", organisation);
  if (organisation.users) {
    return (
      <Layer>
        <Grid>
          <Column lg={12}>
            <TableContainer title="Benutzer" description="Benutzer die dieser Organisation zugeordnet sind" >
              <Table aria-label="Benutzer" size="sm" useZebraStyles={false}>
                <TableHead>
                  <TableRow>
                    <TableCell>E-Mail</TableCell>
                    <TableCell>Letzter Login</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organisation.users.map((user) => {
                    return (
                      <TableRow key={user.model.ID}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{moment(user.last_login).format('DD.MM.YYYY hh:mm')}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Column>
        </Grid>
      </Layer>

    );
  } else {
    return <></>;
  }
}
